// passkey-signal.ts — best-effort local passkey removal via the WebAuthn Signal
// API (PublicKeyCredential.signalUnknownCredential / signalAllAcceptedCredentials).
//
// HONEST LIMITATION: a website CANNOT force-delete a platform passkey. The OS /
// password manager owns the credential. The Signal API is the *sanctioned hint*:
// it tells the authenticator "these credential IDs are no longer valid on this
// RP", which prompts compliant managers (Chrome/Edge/Android, some others) to
// remove or hide the passkey. Safari/Firefox and older browsers lack it — in
// which case we no-op silently. This is "try to remove if possible", never a
// guarantee, and must NEVER block or fail the account-deletion flow.
//
// This module is DOM-free and injectable: it takes a `signaller` object so it
// can be unit-tested without a browser. `buildPasskeySignals` returns the calls
// it *would* make; `runPasskeySignals` executes them defensively.

/** Minimal shape of the WebAuthn Signal API we depend on. All optional. */
export interface PasskeySignaller {
  signalUnknownCredential?(options: { rpId: string; credentialId: string }): Promise<void> | void;
  signalAllAcceptedCredentials?(options: {
    rpId: string;
    userId: string;
    allAcceptedCredentialIds: string[];
  }): Promise<void> | void;
}

export interface PasskeySignalInput {
  rpId: string;
  /** base64url user handle (webauthn user id). Optional; enables the "all accepted = none" call. */
  userId?: string;
  /** base64url credential IDs to invalidate. */
  credentialIds: string[];
}

export type PlannedSignal =
  | { kind: 'unknownCredential'; rpId: string; credentialId: string }
  | { kind: 'allAcceptedCredentials'; rpId: string; userId: string; allAcceptedCredentialIds: string[] };

/**
 * Pure planner: given the input and which methods the signaller supports,
 * returns the ordered list of signal calls to make. No side effects.
 */
export function buildPasskeySignals(input: PasskeySignalInput, signaller: PasskeySignaller | null | undefined): PlannedSignal[] {
  const plan: PlannedSignal[] = [];
  if (!signaller || !input.rpId) return plan;

  if (typeof signaller.signalUnknownCredential === 'function') {
    for (const credentialId of input.credentialIds) {
      if (credentialId) plan.push({ kind: 'unknownCredential', rpId: input.rpId, credentialId });
    }
  }

  // Telling the authenticator that NONE of this user's credentials are accepted
  // anymore is the strongest hint to remove/hide the passkey for this account.
  if (input.userId && typeof signaller.signalAllAcceptedCredentials === 'function') {
    plan.push({ kind: 'allAcceptedCredentials', rpId: input.rpId, userId: input.userId, allAcceptedCredentialIds: [] });
  }

  return plan;
}

/**
 * Executes the planned signals defensively. Never throws; returns how many
 * calls were attempted. Feature-detection is handled by buildPasskeySignals, so
 * an unsupported browser produces an empty plan and this is a graceful no-op.
 */
export async function runPasskeySignals(input: PasskeySignalInput, signaller: PasskeySignaller | null | undefined): Promise<number> {
  const plan = buildPasskeySignals(input, signaller);
  let attempted = 0;
  for (const step of plan) {
    try {
      if (step.kind === 'unknownCredential') {
        await signaller!.signalUnknownCredential!({ rpId: step.rpId, credentialId: step.credentialId });
      } else {
        await signaller!.signalAllAcceptedCredentials!({
          rpId: step.rpId,
          userId: step.userId,
          allAcceptedCredentialIds: step.allAcceptedCredentialIds,
        });
      }
      attempted++;
    } catch {
      // best-effort only; ignore and continue
    }
  }
  return attempted;
}

/**
 * Resolves the browser's PublicKeyCredential as a PasskeySignaller, or null
 * when the Signal API is unavailable. Kept tiny and DOM-touching so the rest of
 * the module stays testable.
 */
export function browserSignaller(): PasskeySignaller | null {
  const g = globalThis as unknown as { PublicKeyCredential?: PasskeySignaller };
  const pkc = g.PublicKeyCredential;
  if (!pkc) return null;
  if (typeof pkc.signalUnknownCredential !== 'function' && typeof pkc.signalAllAcceptedCredentials !== 'function') {
    return null;
  }
  return pkc;
}
